import { Check } from 'lucide-react'
import { createElement, useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'

/** How long the `copied` confirmation state stays true before reverting, in ms. */
const COPIED_RESET_MS = 2000

/**
 * Copies text to the clipboard with a brief `copied` confirmation state and a
 * toast. Centralises the write + confirmation + toast logic shared by copy
 * buttons across the app. `writeText` rejects in insecure contexts or when
 * clipboard permission is denied, so failures surface as an error toast.
 *
 * @returns `copied` — true during the confirmation window after a successful
 *   copy — and `copy(value, label?)`, which performs the copy. `label` names
 *   the copied thing in the toast (e.g. "Email copied"); defaults to "Copied".
 */
export function useCopyToClipboard() {
  const [copied, setCopied] = useState(false)
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined)

  // Clear any pending reset timer if the component unmounts mid-confirmation.
  useEffect(() => () => clearTimeout(timeoutRef.current), [])

  async function copy(value: string, label = 'Copied', successMessage?: string) {
    try {
      await navigator.clipboard.writeText(value)
      setCopied(true)
      clearTimeout(timeoutRef.current)
      timeoutRef.current = setTimeout(() => setCopied(false), COPIED_RESET_MS)
      const message = successMessage ?? (label === 'Copied' ? 'Copied' : `${label} copied`)
      toast(message, { icon: createElement(Check, { className: 'size-4 text-success-fg' }) })
    } catch {
      toast.error(`Failed to copy${label === 'Copied' ? '' : ` ${label.toLowerCase()}`}`)
    }
  }

  return { copied, copy }
}
